/* eslint-disable no-comments/disallowComments */
/**
 * Rich text HTML sanitization contract (kept in sync with backend sitter/common/html_utils.py).
 *
 * Allowed tags:
 * - p, strong, em, h2, ol, ul, li, blockquote, a, hr, br
 *
 * Allowed attributes:
 * - a[href, target, rel]
 *
 * Rules:
 * - Non-allowlisted tags are unwrapped (text is preserved, tag is removed).
 * - Non-allowlisted attributes are removed.
 * - Anchor href protocols are restricted to http, https, and mailto.
 * - target="_blank" forces rel="noopener noreferrer nofollow".
 */
/* eslint-enable no-comments/disallowComments */
const ALLOWED_TAGS = new Set(['A', 'BLOCKQUOTE', 'BR', 'EM', 'H2', 'HR', 'LI', 'OL', 'P', 'STRONG', 'UL']);

const ALLOWED_ANCHOR_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);
const ALLOWED_ANCHOR_ATTRIBUTES = new Set(['href', 'target', 'rel']);

export function sanitizeHtml(html: string): string {
    if (!html) {
        return '';
    }

    const parser = new DOMParser();
    const document = parser.parseFromString(html, 'text/html');

    const sanitizeNode = (node: Node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            const tagName = element.tagName.toUpperCase();

            if (!ALLOWED_TAGS.has(tagName)) {
                const parent = element.parentNode;
                if (parent) {
                    while (element.firstChild) {
                        parent.insertBefore(element.firstChild, element);
                    }
                    parent.removeChild(element);
                }
                return;
            }

            if (tagName === 'A') {
                Array.from(element.attributes).forEach((attribute) => {
                    if (!ALLOWED_ANCHOR_ATTRIBUTES.has(attribute.name)) {
                        element.removeAttribute(attribute.name);
                    }
                });

                const href = element.getAttribute('href');
                if (href) {
                    try {
                        const parsed = new URL(href, window.location.origin);
                        if (!ALLOWED_ANCHOR_PROTOCOLS.has(parsed.protocol)) {
                            element.removeAttribute('href');
                        }
                    } catch {
                        element.removeAttribute('href');
                    }
                }

                if (element.getAttribute('target') === '_blank') {
                    element.setAttribute('rel', 'noopener noreferrer nofollow');
                } else {
                    element.removeAttribute('target');
                    element.removeAttribute('rel');
                }
            } else {
                Array.from(element.attributes).forEach((attribute) => {
                    element.removeAttribute(attribute.name);
                });
            }
        }

        Array.from(node.childNodes).forEach(sanitizeNode);
    };

    Array.from(document.body.childNodes).forEach(sanitizeNode);
    return document.body.innerHTML;
}
